import React, {
  createContext,
  useEffect,
  useMemo,
  useCallback,
  useState,
} from 'react';
import { offersRef } from '../firebase/collections';
import { AuthContext } from './AuthContext';
import { ToastContext } from './ToastContext';
import { acceptOffer } from '../firebase/offers';

export const OfferContext = createContext(null);

function sortOffers(list) {
  return [...list].sort((a, b) => {
    const ta = a.createdAt?.toMillis
      ? a.createdAt.toMillis()
      : a.createdAt?.seconds
        ? a.createdAt.seconds * 1000
        : new Date(a.createdAt || 0).getTime();
    const tb = b.createdAt?.toMillis
      ? b.createdAt.toMillis()
      : b.createdAt?.seconds
        ? b.createdAt.seconds * 1000
        : new Date(b.createdAt || 0).getTime();
    return tb - ta;
  });
}

function OfferProvider({ children }) {
  const { user } = React.useContext(AuthContext);
  const { addToast } = React.useContext(ToastContext);
  const [sentOffers, setSentOffers] = useState([]);
  const [receivedOffers, setReceivedOffers] = useState([]);

  useEffect(() => {
    if (!user?.uid) {
      setSentOffers([]);
      setReceivedOffers([]);
      return;
    }
    const unsubSent = offersRef()
      .where('buyerId', '==', user.uid)
      .onSnapshot(
        (snap) => {
          const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          setSentOffers(sortOffers(list));
        },
        (err) => {
          console.error('OfferContext sentOffers listener error:', err);
          addToast('Could not load your sent offers.', 'error');
          setSentOffers([]);
        }
      );
    const unsubReceived = offersRef()
      .where('sellerId', '==', user.uid)
      .onSnapshot(
        (snap) => {
          const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          setReceivedOffers(sortOffers(list));
        },
        (err) => {
          console.error('OfferContext receivedOffers listener error:', err);
          addToast('Could not load received offers.', 'error');
          setReceivedOffers([]);
        }
      );
    return () => {
      unsubSent();
      unsubReceived();
    };
  }, [user?.uid, addToast]);

  const accept = useCallback(
    async (
      offerId,
      productId,
      buyerId,
      sellerId,
      productName,
      productImage
    ) => {
      try {
        const conversationId = await acceptOffer(
          offerId,
          productId,
          buyerId,
          sellerId,
          productName,
          productImage
        );
        addToast?.('Oferta aceita! Conversa iniciada.', 'success');
        return conversationId;
      } catch (err) {
        console.error('acceptOffer failed:', err);
        addToast?.('Falha ao aceitar oferta.', 'error');
      }
    },
    [addToast]
  );

  const value = useMemo(
    () => ({
      sentOffers,
      receivedOffers,
      acceptOffer: accept,
    }),
    [sentOffers, receivedOffers, accept]
  );

  return (
    <OfferContext.Provider value={value}>{children}</OfferContext.Provider>
  );
}

export default OfferProvider;
